import { ApiOutlined, HistoryOutlined, RobotOutlined } from "@ant-design/icons";
import { AppPaths } from "@p0security/shared/routes/constants";
import { Flex, Menu, Spacing, Tooltip, Typography } from "@p0security/ui";
import { useMemo } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { AgenticPaths } from "../paths";
import type { AgenticOverviewProps } from "./AgenticOverviewContext";

/** The section's tabs, in menu order. */
const TABS = [
  {
    description:
      "MCP tool calls and A2A agent calls observed by this tenant's gateways with event logging configured.",
    icon: <HistoryOutlined aria-hidden="true" />,
    key: AgenticPaths.EventLog,
    label: "Event Log",
  },
  {
    description:
      "MCP clients and agent identities permitted to connect through the gateway.",
    icon: <RobotOutlined aria-hidden="true" />,
    key: AgenticPaths.Clients,
    label: "Clients",
  },
  {
    description: "Endpoints exposed to registered clients by this gateway.",
    icon: <ApiOutlined aria-hidden="true" />,
    key: AgenticPaths.Servers,
    label: "MCP servers",
  },
] as const;

const TabLabel: React.FC<{
  description: string;
  icon: React.ReactNode;
  label: string;
}> = ({ description, icon, label }) => (
  <Tooltip title={description}>
    <Flex align="center" aria-description={description} gap={8}>
      {icon}
      {label}
    </Flex>
  </Tooltip>
);

/** Lays out the agentic section: header, tab menu, and the active tab's route.
 * Data reaches the tabs through the outlet context. */
export const AgenticOverview: React.FC<AgenticOverviewProps> = (props) => {
  const { pathname } = useLocation();
  // The tab is the path segment after `agentic-access` (a `:clientId` may follow it).
  const selectedTab = useMemo(() => {
    const segments = pathname.split("/").filter(Boolean);
    return (
      segments[segments.indexOf(AppPaths.AgenticAccess) + 1] ??
      AgenticPaths.EventLog
    );
  }, [pathname]);

  return (
    <main className="w-full pb-8">
      <Flex gap={32} vertical>
        <header>
          <Flex gap={8} vertical>
            <Spacing asChild m={0}>
              <Typography.Title level={1}>Agentic Access</Typography.Title>
            </Spacing>
            <Typography.Text className="max-w-[720px]" type="secondary">
              Control which clients can access your tools. Clients are the apps
              you register and trust, such as Copilot Studio. MCP servers are
              the tools available through your gateway.
            </Typography.Text>
          </Flex>
        </header>

        <Menu
          mode="horizontal"
          selectedKeys={[selectedTab]}
          items={TABS.map(({ description, icon, key, label }) => ({
            key,
            label: (
              <NavLink to={key}>
                <TabLabel description={description} icon={icon} label={label} />
              </NavLink>
            ),
          }))}
        />
        <Outlet context={props} />
      </Flex>
    </main>
  );
};
